import { createFileRoute, Outlet, redirect } from '@tanstack/react-router';
import { fetchCompanyProfile } from '@/api/companies';
import CompanyHeader from '@/app/(client)/network/-components/CompanyHeader';
import { useCompanyDetail } from '@/app/(client)/network/-hooks/useCompanyDetail';
import type { TabEntry } from '@/components/shared/PageTabs';
import PageTabs from '@/components/shared/PageTabs';
import { useTranslator } from '@/hooks/useTranslator';
import { queryClient } from '@/lib/utils/queryClient';
import { useSessionStore } from '@/store/sessionStore';

export const Route = createFileRoute(
  '/(client)/network/(pages)/(detail)/$companyId',
)({
  beforeLoad: ({ params, location }) => {
    const bare = `/network/${params.companyId}`;
    if (location.pathname === bare || location.pathname === `${bare}/`) {
      throw redirect({
        to: '/network/$companyId/general-info',
        params: { companyId: params.companyId },
      });
    }
  },
  loader: ({ params }) =>
    queryClient.ensureQueryData({
      queryKey: ['network', 'company', params.companyId],
      queryFn: () => fetchCompanyProfile(params.companyId),
    }),
  component: CompanyDetailLayout,
});

function isFeatureEnabled(
  features: Array<{ id: string }> | undefined,
  id: string,
): boolean {
  return features?.some(f => f.id === id) ?? false;
}

function CompanyDetailLayout() {
  const { companyId } = Route.useParams();
  const { getTranslation: t } = useTranslator({ page: 'companyDetail' });
  const { getTranslation: tShared } = useTranslator({ page: 'shared' });
  const { data: company } = useCompanyDetail(companyId);
  const user = useSessionStore(s => s.user);

  const enabledFeatures = user?.company?.enabledFeatures;
  const isOwnCompany = user?.companyId === (company?.forCompanyId ?? companyId);
  const base = `/network/${companyId}`;

  const tabs: TabEntry[] = [
    {
      key: 'general-info',
      label: t('tabs.generalInfo'),
      icon: 'fa-light fa-globe-americas',
      to: `${base}/general-info`,
    },
    {
      key: 'esg-info',
      label: t('tabs.esgInfo'),
      icon: 'fa-light fa-seedling',
      to: `${base}/esg-info`,
    },
    ...(isFeatureEnabled(enabledFeatures, 'risk-analysis-v3') || isOwnCompany
      ? [
          {
            key: 'risk-profile',
            label: t('tabs.riskProfile'),
            icon: 'fa-light fa-mountains',
            to: `${base}/risk-profile`,
          } as TabEntry,
        ]
      : []),
    {
      key: 'claims',
      label: t('tabs.certifications'),
      icon: 'fa-light fa-file-certificate',
      to: `${base}/claims`,
    },
    {
      key: 'audits',
      label: t('tabs.audits'),
      icon: 'fa-light fa-user-hard-hat',
      to: `${base}/audits`,
    },
    {
      key: 'documents',
      label: t('tabs.documents'),
      icon: 'fa-light fa-books',
      to: `${base}/documents`,
    },
    {
      key: 'users',
      label: t('tabs.users'),
      icon: 'fa-light fa-users',
      to: `${base}/users`,
    },
    {
      key: 'products',
      label: t('tabs.products'),
      icon: 'fa-light fa-shirt',
      to: `${base}/products`,
    },
    {
      key: 'integrations',
      label: t('tabs.integrations'),
      icon: 'fa-light fa-plug',
      to: `${base}/integrations`,
    },
    ...(!isOwnCompany
      ? [
          {
            key: 'questionnaire',
            label: t('tabs.questionnaire'),
            icon: 'fa-light fa-tachometer',
            to: `${base}/questionnaire`,
          } as TabEntry,
        ]
      : []),
    ...(isOwnCompany
      ? [
          {
            key: 'auditors',
            label: t('tabs.auditors'),
            icon: 'fa-light fa-people-arrows',
            to: `${base}/auditors`,
          } as TabEntry,
        ]
      : []),
    ...(isFeatureEnabled(enabledFeatures, 'custom-information-fields') &&
    !isOwnCompany
      ? [
          {
            key: 'custom-information',
            label: t('tabs.customInformation'),
            icon: 'fa-light fa-database',
            to: `${base}/custom-information`,
          } as TabEntry,
        ]
      : []),
    ...(isFeatureEnabled(enabledFeatures, 'custom-information-fields') &&
    isOwnCompany
      ? [
          {
            key: 'custom-information-fields',
            label: t('tabs.customInformationFields'),
            icon: 'fa-light fa-tools',
            to: `${base}/custom-information-fields`,
          } as TabEntry,
        ]
      : []),
    ...(!isOwnCompany
      ? [
          {
            key: 'webshop-profile',
            label: t('tabs.webshopProfile'),
            icon: 'fa-light fa-sign',
            to: `${base}/webshop-profile`,
          } as TabEntry,
          {
            key: 'slcp',
            label: t('tabs.slcp'),
            icon: 'fa-light fa-chart-line',
            to: `${base}/slcp`,
          } as TabEntry,
        ]
      : []),
    ...(isOwnCompany
      ? [
          {
            key: 'badges',
            label: t('tabs.badges'),
            icon: 'fa-light fa-award',
            to: `${base}/badges`,
          } as TabEntry,
        ]
      : []),
  ];

  return (
    <div className='flex flex-col flex-1 min-h-0 overflow-y-auto'>
      <CompanyHeader
        company={company}
        companyId={companyId}
        isOwnCompany={isOwnCompany}
      />
      <div className='sticky top-0 z-20 bg-background'>
        <PageTabs tabs={tabs} mode='path' moreLabel={tShared('moreTabs')} />
      </div>
      <Outlet />
    </div>
  );
}
